import { useRef, useState } from "react";
import { Camera, X, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

const WORKER_BASE = (
  process.env.REACT_APP_PAPI_WORKER_URL
  || "https://preguntale-a-papi.guendelman.workers.dev"
).replace(/\/ask-papi\/?$/, "").replace(/\/+$/, "");

// Comprime una imagen del canvas a JPEG con quality 0.85. Redimensiona si es
// muy grande (max 1600px por lado) — así no explotamos R2 con fotos de 12MP.
async function compressImage(file, maxSize = 1600, quality = 0.85) {
  const img = document.createElement("img");
  const url = URL.createObjectURL(file);
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  URL.revokeObjectURL(url);

  let { width, height } = img;
  if (width > maxSize || height > maxSize) {
    const scale = maxSize / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

export default function PhotoUpload({
  open,
  onClose,
  member,
  displayName,
  recipeSlug,
  recipeName,
  onUploaded,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  if (!open) return null;

  const handleFile = async (f) => {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
      toast.error("Formato no soportado (JPG, PNG o WEBP)");
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const dataUrl = await blobToDataUrl(compressed);
      const res = await fetch(`${WORKER_BASE}/upload-plate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          member,
          displayName,
          recipe_slug: recipeSlug,
          recipe_name: recipeName,
          data_url: dataUrl,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.detail || json.error || "upload_failed");
      toast.success("¡Foto subida a la galería familiar!");
      onUploaded?.(json.plate);
      onClose();
    } catch (e) {
      toast.error(`No se pudo subir: ${e.message || e}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setFile(null);
    onClose();
  };

  return (
    <div
      data-testid="photo-upload-modal"
      className="fixed inset-0 z-50 bg-cocoa/60 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-paper rounded-2xl border border-line w-full max-w-md p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display italic text-cocoa text-2xl">Foto del plato</h3>
          <button
            data-testid="photo-upload-close"
            onClick={handleClose}
            disabled={uploading}
            className="p-2 rounded-full bg-cream border border-line"
          >
            <X className="w-4 h-4 text-cocoa" />
          </button>
        </div>

        <p className="font-serif text-cocoa-soft text-sm italic mb-4">
          Sube una foto pa' que toda la familia vea cómo te quedó.
        </p>

        {!preview && (
          <>
            <input
              ref={inputRef}
              data-testid="photo-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
            />
            <button
              data-testid="photo-pick-btn"
              onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-amber-deep/40 bg-cream rounded-xl py-10 flex flex-col items-center gap-2 text-cocoa hover:border-amber-deep transition"
            >
              <Camera className="w-8 h-8 text-amber-deep" />
              <span className="font-serif text-base">Tomar o elegir foto</span>
              <span className="text-xs italic text-cocoa-soft">JPG, PNG o WEBP · máx 8 MB</span>
            </button>
          </>
        )}

        {preview && (
          <>
            <div className="rounded-xl overflow-hidden border border-line mb-4">
              <img src={preview} alt="Vista previa" className="w-full aspect-square object-cover" />
            </div>
            <div className="flex gap-2">
              <button
                data-testid="photo-retry-btn"
                onClick={() => { if (preview) URL.revokeObjectURL(preview); setPreview(null); setFile(null); }}
                disabled={uploading}
                className="btn-ghost flex-1"
              >
                Otra
              </button>
              <button
                data-testid="photo-upload-submit"
                onClick={handleUpload}
                disabled={uploading}
                className="btn-primary flex-1"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Subiendo…" : "Subir"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
